import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ── Todos os status do pipeline ───────────────────────────────────────────────
const GRUPOS = [
  {
    label: 'Leads',
    items: [
      { key:'pre_venda',           label:'Pré Venda',            color:'#64748b' },
      { key:'aguardando_vendedor', label:'Aguardando Vendedor',   color:'#94a3b8' },
      { key:'visita',            label:'Visita Agendada',      color:'#a78bfa' },
      { key:'proposta',          label:'Proposta Enviada',     color:'#fb923c' },
      { key:'fechado',           label:'Venda Fechada',        color:'#4ade80' },
      { key:'perdido',           label:'Desistiu da Compra',   color:'#f87171' },
      { key:'em_espera',         label:'Aguardando Cliente',   color:'#fbbf24' },
    ],
  },
  {
    label: 'Pós Venda',
    items: [
      { key:'na_transportadora', label:'Na Transportadora',    color:'#67e8f9' },
      { key:'finalizado',        label:'Finalizado',           color:'#34d399' },
    ],
  },
  {
    label: 'Engenheiro',
    items: [
      { key:'aguardando_engenheiro',   label:'Aguardando Engenheiro',   color:'#64748b' },
      { key:'realizando_planejamento', label:'Realizando Planejamento', color:'#60a5fa' },
      { key:'aguardando_cemig',        label:'Aguardando CEMIG',        color:'#fbbf24' },
      { key:'documentacao_aprovada',   label:'Documentação Aprovada',   color:'#a78bfa' },
      { key:'vistoria_feita',          label:'Vistoria Feita',          color:'#4ade80' },
    ],
  },
  {
    label: 'Instalador',
    items: [
      { key:'aguardando_instalador',  label:'Aguardando Instalador', color:'#64748b' },
      { key:'instalacao_programada',  label:'Instalação Programada', color:'#60a5fa' },
      { key:'instalando',             label:'Instalando',            color:'#a78bfa' },
      { key:'instalacao_concluida',   label:'Instalação Concluída',  color:'#4ade80' },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      { key:'aguardando_pagamento', label:'Aguardando Pagamento', color:'#fbbf24' },
      { key:'pago',                 label:'Pago',                 color:'#4ade80' },
    ],
  },
];

const ALL_STATUS_MAP = {};
GRUPOS.forEach(g => g.items.forEach(i => { ALL_STATUS_MAP[i.key] = i; }));


const S = {
  heading:   { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub:       { fontSize:13, color:'#475569', marginBottom:16 },
  searchWrap:{ marginBottom:14 },
  searchInput: {
    width:'100%', maxWidth:340, padding:'8px 14px', borderRadius:8,
    border:'1px solid #1e3a5f', background:'#0b1526', color:'#cbd5e1',
    fontSize:13, outline:'none', boxSizing:'border-box',
  },
  groupLabel:{ fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase',
               letterSpacing:'0.05em', marginBottom:5, marginTop:10 },
  filters:   { display:'flex', gap:7, flexWrap:'wrap', marginBottom:4 },
  filterBtn: (active, color) => ({
    padding:'5px 12px', borderRadius:20, border:`1px solid ${color}55`,
    background: active ? `${color}25` : 'transparent',
    color: active ? color : '#64748b', fontSize:11, fontWeight:600,
    cursor:'pointer', transition:'all .15s',
  }),
  allBtn: (active) => ({
    padding:'5px 14px', borderRadius:20,
    border:'1px solid #f6c90e55',
    background: active ? '#f6c90e22' : 'transparent',
    color: active ? '#f6c90e' : '#94a3b8',
    fontSize:11, fontWeight:700, cursor:'pointer',
  }),
  mapWrap:   { borderRadius:14, overflow:'hidden', border:'1px solid #1a2035', height:500, marginTop:14 },
  countBadge:{ fontSize:12, color:'#64748b', marginBottom:6 },
};

export default function MapaProjetos() {
  const navigate = useNavigate();
  const [allPontos, setAllPontos]       = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput]   = useState('');
  const [showSugg, setShowSugg]         = useState(false);
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef     = useRef([]);
  const searchWrapRef  = useRef(null);

  // Expõe navigate globalmente para o popup do Leaflet (HTML puro)
  useEffect(() => {
    window.__crmNavigate = navigate;
    return () => { delete window.__crmNavigate; };
  }, [navigate]);

  // Carrega todos os pontos uma vez
  const loadData = useCallback(() => {
    api.get('/mapa/').then(r => setAllPontos(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Filtragem local: status + busca (para os pontos no mapa)
  const pontos = allPontos.filter(p => {
    const matchStatus = !statusFilter || p.status === statusFilter;
    const matchSearch = !searchInput  || p.cliente?.toLowerCase().includes(searchInput.toLowerCase());
    return matchStatus && matchSearch;
  });

  // ── Agrupa por cliente_id para o autocomplete (sem duplicatas) ───────────────
  const clientesAgrupados = useMemo(() => {
    const map = {};
    allPontos.forEach(p => {
      if (!p.lat || !p.lng) return;
      if (!map[p.cliente_id]) {
        map[p.cliente_id] = {
          cliente_id: p.cliente_id,
          cliente:    p.cliente,
          cidade:     p.cidade,
          estado:     p.estado,
          lat:        p.lat,
          lng:        p.lng,
        };
      }
    });
    return Object.values(map);
  }, [allPontos]);

  // Sugestões filtradas por nome (máx 8, agrupadas por cliente)
  const suggestions = useMemo(() => {
    if (searchInput.length < 1) return [];
    return clientesAgrupados
      .filter(c => c.cliente?.toLowerCase().includes(searchInput.toLowerCase()))
      .slice(0, 8);
  }, [searchInput, clientesAgrupados]);

  // Fecha sugestões ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target))
        setShowSugg(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Zoom no endereço do cliente + abre popup do primeiro marcador encontrado
  const focusPonto = useCallback((cliente) => {
    const map = mapInstanceRef.current;
    if (!map || !cliente.lat || !cliente.lng) return;
    map.setView([cliente.lat, cliente.lng], 18, { animate: true });
    const entry = markersRef.current.find(e => e.ponto.cliente_id === cliente.cliente_id);
    if (entry) setTimeout(() => entry.marker.openPopup(), 400);
    setShowSugg(false);
    setSearchInput(cliente.cliente);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && suggestions.length > 0) focusPonto(suggestions[0]);
    if (e.key === 'Escape') setShowSugg(false);
  };

  // Init mapa
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    if (!L) return;
    const map = L.map(mapRef.current).setView([-18.9186, -48.2772], 7);
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution:'© Esri, Maxar, Earthstar Geographics', maxZoom:19 }
    ).addTo(map);
    mapInstanceRef.current = map;
  }, []);

  // Atualizar marcadores
  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;
    markersRef.current.forEach(e => e.marker.remove());
    markersRef.current = [];

    const _VERDE    = new Set(['fechado','finalizado','vistoria_feita','instalacao_concluida','pago','concluido']);
    const _VERMELHO = new Set(['aguardando_vendedor','aguardando_engenheiro','aguardando_instalador',
                               'aguardando_pagamento','aguardando_cemig','aguardando','perdido','cancelado']);
    const stCor = (s) => {
      if (!s) return '#334155';
      if (_VERDE.has(s))    return '#4ade80';
      if (_VERMELHO.has(s)) return '#f87171';
      return '#f6c90e';
    };

    pontos.forEach(p => {
      if (!p.lat || !p.lng) return;
      const info  = ALL_STATUS_MAP[p.status];
      const color = info?.color || '#f6c90e';
      const icon  = L.divIcon({
        className: '',
        html: `<div style="
          width:13px;height:13px;border-radius:50%;
          background:${color};border:2.5px solid white;
          box-shadow:0 2px 8px rgba(0,0,0,.5);
        "></div>`,
        iconSize:[13,13], iconAnchor:[6,6],
      });

      const circles = [['V', p.v_status],['E', p.e_status],['P', p.p_status],['I', p.i_status],['F', p.f_status]]
        .map(([l, s]) => {
          const c = stCor(s);
          return `<div style="width:22px;height:22px;border-radius:50%;background:${c};display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#0d1424;">${l}</div>`;
        }).join('');

      const popup = `
        <div style="font-family:sans-serif;min-width:200px;font-size:12px">
          <a href="#" onclick="window.__crmNavigate('/jornada/${p.cliente_id}');return false;"
             style="font-size:13px;font-weight:700;color:#f6c90e;text-decoration:none;cursor:pointer;">
            ${p.cliente}
          </a><br/>
          <span style="color:#888">${p.cidade || ''}${p.estado ? '/'+p.estado : ''}</span>
          <div style="display:flex;gap:5px;margin:8px 0 2px;">${circles}</div>
          <hr style="margin:5px 0;border-color:#eee"/>
          <b>Responsável:</b> ${p.responsavel || '—'}<br/>
          ${p.potencia_kwp != null ? `<b>Potência:</b> ${p.potencia_kwp} kWp<br/>` : ''}
          ${p.valor_total  != null ? `<b>Valor:</b> R$ ${Number(p.valor_total).toLocaleString('pt-BR')}<br/>` : ''}
          ${p.data_inicio   ? `<b>Início:</b> ${p.data_inicio}<br/>`    : ''}
          ${p.data_prevista ? `<b>Previsão:</b> ${p.data_prevista}` : ''}
        </div>`;
      const marker = L.marker([p.lat, p.lng], { icon })
        .addTo(mapInstanceRef.current)
        .bindPopup(popup);
      markersRef.current.push({ ponto: p, marker });
    });
  }, [pontos]);

  // Carregar Leaflet se necessário
  useEffect(() => {
    if (window.L) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = loadData;
    document.head.appendChild(script);
  }, []);

  // Contagem por status (dos dados atuais)
  const countMap = {};
  pontos.forEach(p => { countMap[p.status] = (countMap[p.status] || 0) + 1; });

  return (
    <div>
      <div style={S.heading}>Mapa de Projetos</div>
      <div style={S.sub}>
        Visualize toda a pipeline no mapa. Clique no ponto para ver os detalhes.
      </div>

      {/* Busca por nome com autocomplete */}
      <div ref={searchWrapRef} style={{ ...S.searchWrap, position:'relative' }}>
        <input
          style={S.searchInput}
          placeholder="🔍  Buscar cliente pelo nome..."
          value={searchInput}
          onChange={e => { setSearchInput(e.target.value); setShowSugg(true); }}
          onFocus={() => setShowSugg(true)}
          onKeyDown={handleKeyDown}
        />
        {showSugg && suggestions.length > 0 && (
          <div style={{
            position:'absolute', top:'100%', left:0, width:'100%', maxWidth:340,
            background:'#0e1420', border:'1px solid #1a2035', borderRadius:8,
            zIndex:1000, maxHeight:320, overflowY:'auto', marginTop:4,
            boxShadow:'0 8px 24px rgba(0,0,0,.6)',
          }}>
            {suggestions.map((c) => (
              <div
                key={c.cliente_id}
                onClick={() => focusPonto(c)}
                onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
                style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #0f1117' }}
              >
                <div style={{ color:'#e2e8f0', fontWeight:600, fontSize:13 }}>{c.cliente}</div>
                <div style={{ color:'#475569', fontSize:11, marginTop:2 }}>
                  {c.cidade}{c.estado ? `/${c.estado}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filtros por status */}
      <div style={S.filters}>
        <button style={S.allBtn(!statusFilter)} onClick={() => setStatusFilter('')}>
          Todos ({pontos.length})
        </button>
      </div>

      {GRUPOS.map(grupo => (
        <div key={grupo.label}>
          <div style={S.groupLabel}>{grupo.label}</div>
          <div style={S.filters}>
            {grupo.items.map(({ key, label, color }) => {
              const cnt = countMap[key];
              return (
                <button
                  key={key}
                  style={S.filterBtn(statusFilter === key, color)}
                  onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
                >
                  {label}{cnt ? ` (${cnt})` : ''}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div style={S.mapWrap}>
        <div ref={mapRef} style={{ width:'100%', height:'100%' }} />
      </div>
    </div>
  );
}
